import React, { useState, useEffect, useMemo } from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import TenantsManagementTab from "./components/TenantsManagementTab";
import ImporterStatusTab from "./components/ImporterStatusTab";
import LogAgents from "./components/LogAgents";
import ProductsTab from "./components/ProductsTab";
import ChatTester from "./components/ChatTester";
import OnboardTenantTab from "./components/OnboardTenants/OnboardTenantTab";
import axiosInstance from "../src/axiosInstance";
import { Helmet } from "react-helmet-async";
import UsersManagementTab from "./components/UsersManagementTab";
import ApisTab from "./components/ApisTab";
import StatusPage from "./components/StatusPage";
import FaqHighlightsTab from "./components/FaqHighlightsTab";
import ExperimentalSearchTab from "./components/ExperimentalSearchTab";
import FloatingNotes from "./components/FloatingNotes";
import AssistantChat from "./components/AssistantChat";
import {
  clearRememberedTenantId,
  getRememberedTenantId,
  rememberTenantId,
} from "./utils/tenantSelectionCookie";
import {
  canUseAiAssistant,
  isAdminUser,
  isFullAdmin,
} from "./config/adminUsers";
import { getUserEmail } from "./utils/getUserEmail";
import {
  AccountTreeOutlined,
  ApiOutlined,
  CloudSyncOutlined,
  LogoutOutlined,
  MonitorHeartOutlined,
  AutoAwesomeOutlined,
  PeopleOutlined,
  PersonOutlined,
  PersonAddOutlined,
  SettingsOutlined,
  ShoppingCart,
  ScienceOutlined,
  StorefrontOutlined,
} from "@mui/icons-material";

const navigationTabs = [
  { label: "Tenant Management", shortLabel: "Tenants", icon: <SettingsOutlined fontSize="small" />, path: "/dashboard/tenant-management" },
  { label: "Importer Status", shortLabel: "Importer", icon: <CloudSyncOutlined fontSize="small" />, path: "/dashboard/importer-status" },
  { label: "Agents trace", shortLabel: "Agents", icon: <AccountTreeOutlined fontSize="small" />, path: "/dashboard/agents-trace" },
  { label: "Products", shortLabel: "Products", icon: <StorefrontOutlined fontSize="small" />, path: "/dashboard/products" },
  { label: "Onboard Tenant", shortLabel: "Onboard", icon: <PersonAddOutlined fontSize="small" />, path: "/dashboard/onboard-tenant" },
  { label: "Users Management", shortLabel: "Users", icon: <PeopleOutlined fontSize="small" />, path: "/dashboard/users-management", fullAdminOnly: true },
  { label: "APIs", shortLabel: "APIs", icon: <ApiOutlined fontSize="small" />, path: "/dashboard/apis" },

  { label: "Status", shortLabel: "Status", icon: <MonitorHeartOutlined fontSize="small" />, path: "/dashboard/services-status" },
  { label: "FAQs", shortLabel: "FAQs", icon: <AutoAwesomeOutlined fontSize="small" />, path: "/dashboard/faqs" },
  { label: "Experimental", shortLabel: "Experimental", icon: <ScienceOutlined fontSize="small" />, path: "/dashboard/experimental" },
];

const TENANT_MANAGEMENT_PATH = "/dashboard/tenant-management";
const IMPORTER_STATUS_PATH = "/dashboard/importer-status";
const PRODUCTS_PATH = "/dashboard/products";
const AGENTS_PATH = "/dashboard/agents-trace";

const getAssistantPage = (pathname) => {
  if (
    pathname === IMPORTER_STATUS_PATH ||
    pathname.startsWith(`${IMPORTER_STATUS_PATH}/`)
  ) {
    return "importer";
  }

  if (pathname === PRODUCTS_PATH || pathname.startsWith(`${PRODUCTS_PATH}/`)) {
    return "products";
  }

  if (pathname === AGENTS_PATH || pathname.startsWith(`${AGENTS_PATH}/`)) {
    return "agents";
  }

  if (
    pathname === TENANT_MANAGEMENT_PATH ||
    pathname.startsWith(`${TENANT_MANAGEMENT_PATH}/`)
  ) {
    return "tenants";
  }

  return null;
};

const ImportProgressDashboard = ({ apiKey, onLogout, isIframe }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(() => getRememberedTenantId());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [assistantPageContext, setAssistantPageContext] = useState({
    Page: "tenants",
    areaId: "tenant-general",
    SectionId: "tenant-general-overview",
    SubsectionId: null,
  });

  const userEmail = getUserEmail();
  const isAdmin = isAdminUser(userEmail);
  const hasFullAdmin = isFullAdmin(userEmail);
  const hasAiAssistantAccess = canUseAiAssistant(userEmail);
  const assistantPage = getAssistantPage(location.pathname);

  const visibleTabs = useMemo(
    () => navigationTabs.filter((tab) => {
      if (tab.fullAdminOnly) return hasFullAdmin;
      if (tab.adminOnly) return isAdmin;
      return true;
    }),
    [isAdmin, hasFullAdmin]
  );

  const pathToTabIndex = useMemo(
    () => Object.fromEntries(visibleTabs.map((tab, i) => [tab.path, i])),
    [visibleTabs]
  );

  // Calculate current tab from URL
  const currentTab = pathToTabIndex[location.pathname] ?? 0;
  const selectedTenantName =
    tenants.find((tenant) => tenant.id === selectedTenant)?.name || "";

  const fetchTenants = async () => {
    try {
      const response = await axiosInstance.get("/tenant/alltenants");
      setTenants(response.data);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      if (error.config && error.config.headers) {
        console.log("Request Headers:", error.config.headers);
      }
    }
  };

  useEffect(() => {
    // Set headers on the specific instance used by this component
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${apiKey}`;
    axiosInstance.defaults.headers.common["t-api-key"] = apiKey;
    fetchTenants();
  }, [apiKey]);

  // Fetch tenants when path changes (not when tab state changes!)
  useEffect(() => {
    fetchTenants();
  }, [location.pathname]);

  useEffect(() => {
    if (selectedTenant) {
      rememberTenantId(selectedTenant);
    } else {
      clearRememberedTenantId();
    }
  }, [selectedTenant]);

  useEffect(() => {
    const rememberedTenantId = getRememberedTenantId();

    if (rememberedTenantId && tenants.some((tenant) => tenant.id === rememberedTenantId)) {
      if (selectedTenant !== rememberedTenantId) {
        setSelectedTenant(rememberedTenantId);
      }
      return;
    }

    if (selectedTenant && tenants.length > 0 && !tenants.some((tenant) => tenant.id === selectedTenant)) {
      setSelectedTenant("");
    }
  }, [tenants, selectedTenant]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRememberedTenantId();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleTabChange = (event, newValue) => {
    navigate(visibleTabs[newValue].path);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        overflowX: "hidden",
        background: "radial-gradient(circle at top center, rgba(255, 192, 155, 0.20), rgba(255,255,255,0) 34%), linear-gradient(180deg, #fffaf6 0%, #fdf6f2 36%, #f8f9fc 100%)",
        px: { xs: 1, sm: 1.5, md: 2.5 },
        pt: { xs: 1, md: 2 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1520px",
          mx: "auto",
        }}
      >
        <Helmet>
          <title>Management</title>
          <link rel="icon" type="image/png" href="/monitoring-icon.png" />
        </Helmet>

        <Paper
          sx={{
            position: "sticky",
            top: 12,
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, md: 2 },
            px: { xs: 0.75, md: 1.75 },
            py: { xs: 0.75, md: 1.1 },
            mb: { xs: 2, md: 2.5 },
            borderRadius: { xs: "18px", md: "24px" },
            border: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 20px 52px rgba(236, 123, 55, 0.12)",
            overflow: "hidden",
          }}
        >
          {!isIframe && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
                px: { xs: 0.5, md: 0.75 },
              }}
            >
              <Box
                component="img"
                src="https://devstoragegreenback.blob.core.windows.net/dev-image-container/logo-preezie.png"
                alt="Preezie"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "3px",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#202531",
                  letterSpacing: "-0.02em",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Preezie
              </Typography>
            </Box>
          )}

          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "999px",
                background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
              },
              "& .MuiTabs-flexContainer": {
                gap: { xs: 0, md: 0.5 },
              },
              "& .MuiTabs-scrollButtons": {
                color: "#f97316",
                "&.Mui-disabled": { opacity: 0.3 },
              },
            }}
          >
            {visibleTabs.map((item) => (
              <Tab
                key={item.label}
                label={
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    {item.shortLabel}
                  </Box>
                }
                icon={item.icon}
                iconPosition="start"
                aria-label={item.label}
                sx={{
                  minHeight: 0,
                  minWidth: { xs: 44, sm: "auto" },
                  textTransform: "none",
                  px: { xs: 0.75, sm: 1.25, md: 1.6 },
                  py: { xs: 1, md: 1.35 },
                  borderRadius: "14px",
                  color: "#4b5563",
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.95rem" },
                  fontWeight: 600,
                  gap: 0.9,
                  transition: "all 0.2s ease",
                  "& .MuiTab-iconWrapper": {
                    mb: "0 !important",
                    mr: { xs: 0, sm: 0.75 },
                  },
                  "&:hover": {
                    color: "#f97316",
                    backgroundColor: "rgba(255, 247, 241, 0.72)",
                  },
                  "&.Mui-selected": {
                    color: "#f97316",
                  },
                }}
              />
            ))}
          </Tabs>

          <Box
            sx={{
              display: isIframe ? "none" : "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              pl: { xs: 0, md: 1 },
            }}
          >
            {!isIframe && (
              <>
                <IconButton
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    sx={{
                      width: 42,
                      height: 42,
                      color: "#f97316",
                      background:
                        "linear-gradient(180deg, #fff1e8 0%, #ffe7d8 100%)",
                      border: "1px solid rgba(255, 132, 76, 0.12)",
                      cursor: "pointer",
                    }}
                  >
                    <PersonOutlined fontSize="small" />
                  </Avatar>
                </IconButton>
                <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: "14px",
                      border: "1px solid rgba(255, 132, 76, 0.10)",
                      boxShadow: "0 20px 44px rgba(236, 123, 55, 0.12)",
                      minWidth: 160,
                    },
                  },
                }}
              >
                {hasFullAdmin && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      navigate("/dashboard/widget-tester");
                    }}
                    sx={{
                      borderRadius: "10px",
                      mx: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(255, 132, 76, 0.08)",
                        color: "#f97316",
                        "& .MuiListItemIcon-root": { color: "#f97316" },
                      },
                    }}
                  >
                    <ListItemIcon>
                      <ShoppingCart fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Widget Tester</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onLogout();
                  }}
                  sx={{
                    borderRadius: "10px",
                    mx: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                      color: "#ef4444",
                      "& .MuiListItemIcon-root": { color: "#ef4444" },
                    },
                  }}
                >
                  <ListItemIcon>
                    <LogoutOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Paper>

        <Box
          sx={{
            px: { xs: 0, md: 0.5 },
          }}
        >
          <Routes>
            <Route
              path="tenant-management"
              element={
                <TenantsManagementTab
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                  fetchTenants={fetchTenants}
                  tenants={tenants}
                  onAssistantContextChange={setAssistantPageContext}
                />
              }
            />
            <Route
              path="importer-status"
              element={
                <ImporterStatusTab
                  apiKey={apiKey}
                  tenants={tenants}
                  selectedTenantId={selectedTenant}
                  setSelectedTenantId={setSelectedTenant}
                />
              }
            />
            <Route
              path="agents-trace"
              element={<LogAgents apiKey={apiKey} />}
            />
            <Route
              path="products"
              element={
                <ProductsTab
                  apiKey={apiKey}
                  tenants={tenants}
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                />
              }
            />
            <Route
              path="widget-tester"
              element={
                isAdmin ? (
                <ChatTester
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                  fetchTenants={fetchTenants}
                  tenants={tenants}
                />
                ) : (
                  <Navigate to="tenant-management" replace />
                )
              }
            />
            <Route
              path="onboard-tenant"
              element={<OnboardTenantTab apiKey={apiKey} />}
            />
            <Route
              path="users-management"
              element={
                hasFullAdmin ? (
                  <UsersManagementTab tenants={tenants} />
                ) : (
                  <Navigate to="tenant-management" replace />
                )
              }
            />
            <Route
              path="apis"
              element={
                <ApisTab
                  tenants={tenants}
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                />
              }
            />
            <Route
              path="services-status"
              element={<StatusPage />}
            />
            <Route
              path="faqs"
              element={
                <FaqHighlightsTab
                  tenants={tenants}
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                />
              }
            />
            <Route
              path="experimental"
              element={
                <ExperimentalSearchTab
                  tenants={tenants}
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                />
              }
            />
            <Route
              path="/"
              element={<Navigate to="tenant-management" replace />}
            />
          </Routes>
        </Box>

        {process.env.REACT_APP_VERSION && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "#9ca3af",
              mt: 4,
              pb: 1,
            }}
          >
            v{process.env.REACT_APP_VERSION}
          </Typography>
        )}
      </Box>
      {hasAiAssistantAccess && assistantPage && (
        <AssistantChat
          key={assistantPage}
          tenantName={selectedTenantName}
          context={
            assistantPage === "tenants"
              ? {
                  ...assistantPageContext,
                  TenantId: selectedTenant || null,
                }
              : {
                  Page: assistantPage,
                  TenantId:
                    assistantPage === "products" || assistantPage === "importer"
                      ? selectedTenant || null
                      : null,
                  areaId: null,
                  SectionId: null,
                  SubsectionId: null,
                }
          }
        />
      )}
      <FloatingNotes />
    </Box>
  );
};

export default ImportProgressDashboard;
